import React from "react";
import { reportError } from "@/lib/observability";

/**
 * The page shown when rendering itself breaks.
 *
 * Without this, one exception in one component unmounts the whole tree and
 * the traveller is left with a white screen and nothing to do. A class
 * component because that is the only kind that can catch a render error.
 *
 * The language is read from storage rather than the settings context: the
 * boundary sits above every provider, and a crash inside a provider must
 * still be caught. The technical message is never shown — it goes to the
 * error report, where the person who can act on it will read it.
 */
const COPY = {
  fr: {
    title: "Une erreur est survenue",
    body: "La page n'a pas pu s'afficher. Rechargez-la ; si le problème persiste, écrivez-nous depuis la page d'aide.",
    reload: "Recharger la page",
    home: "Retour à l'accueil",
  },
  en: {
    title: "Something went wrong",
    body: "The page could not be displayed. Reload it; if the problem persists, write to us from the help page.",
    reload: "Reload the page",
    home: "Back to home",
  },
};

function language() {
  try {
    return localStorage.getItem("touki_lang") === "en" ? "en" : "fr";
  } catch {
    return "fr";
  }
}

export default class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error, info) {
    (this.props.report || reportError)(error, { where: "render", componentStack: info?.componentStack });
  }

  render() {
    if (!this.state.failed) return this.props.children;
    const t = COPY[language()];
    return (
      <div role="alert" data-testid="crash-page" className="flex min-h-[60vh] flex-col items-center justify-center gap-4 px-6 py-16 text-center">
        <h1 className="font-display text-heading-xl font-extrabold text-touki-black">{t.title}</h1>
        <p className="max-w-md text-touki-muted">{t.body}</p>
        <div className="flex flex-wrap justify-center gap-3">
          <button type="button" onClick={() => window.location.reload()}
            className="rounded-lg bg-touki-red px-5 py-2.5 font-semibold text-white hover:bg-touki-red-dark">
            {t.reload}
          </button>
          <a href="/" className="rounded-lg border border-touki px-5 py-2.5 font-semibold text-touki-black hover:bg-touki-light">
            {t.home}
          </a>
        </div>
      </div>
    );
  }
}
